import React, { useState } from 'react'
import { Button } from './Button'
import { authFetch } from '../lib/authFetch'

interface AddRFPModalProps {
  onClose: () => void
  onCreated: (rfpId: number, message?: string) => void
}

const PLATFORMS = [
  {
    id: 'Catalant',
    label: 'Catalant',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    color: 'text-blue-600',
    activeBg: 'border-blue-500 bg-blue-50',
    disabled: false,
  },
  {
    id: 'Umbrex',
    label: 'Umbrex',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
    color: 'text-violet-600',
    activeBg: 'border-violet-500 bg-violet-50',
    disabled: false,
  },
  {
    id: 'BTG',
    label: 'BTG',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
    color: 'text-emerald-600',
    activeBg: 'border-emerald-500 bg-emerald-50',
    disabled: true,
  },
]

const MAX_CHARS = 50000
const MAX_NOTES_CHARS = 4000

export default function AddRFPModal({ onClose, onCreated }: AddRFPModalProps) {
  const [rfpText, setRfpText] = useState('')
  const [bidderNotes, setBidderNotes] = useState('')
  const [platform, setPlatform] = useState('Catalant')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const charPct = Math.min(100, (rfpText.length / MAX_CHARS) * 100)
  const charColor = charPct > 90 ? 'bg-red-500' : charPct > 70 ? 'bg-amber-400' : 'bg-blue-500'

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [loading, onClose])

  const createRfp = async () => {
    const response = await authFetch('/api/v1/rfps', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        platform_type: platform,
        input_text: rfpText,
        ...(platform === 'Umbrex' && bidderNotes.trim() ? { bidder_notes: bidderNotes.trim() } : {}),
      }),
    })

    let payload: Record<string, unknown> | null = null
    try { payload = (await response.json()) as Record<string, unknown> } catch { payload = null }

    if (!response.ok) {
      const detail = typeof payload?.detail === 'string' ? payload.detail : ''
      throw new Error(detail || `Failed to create RFP: ${response.status} ${response.statusText}`)
    }
    return payload
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!rfpText.trim()) { setError('Please paste the RFP text before submitting.'); return }

    try {
      setLoading(true)
      setError(null)

      let data: Record<string, unknown> | null = null
      try {
        data = await createRfp()
      } catch (initialErr) {
        const msg = (initialErr as Error).message || ''
        if (/networkerror|failed to fetch|network request failed/i.test(msg)) {
          await new Promise((resolve) => setTimeout(resolve, 350))
          data = await createRfp()
        } else {
          throw initialErr
        }
      }

      if (!data || typeof data.rfp_id !== 'number') {
        throw new Error('Submission succeeded but no RFP ID was returned.')
      }
      onCreated(data.rfp_id, typeof data.message === 'string' ? data.message : undefined)
    } catch (err) {
      const message = (err as Error).message || 'Failed to create RFP.'
      setError(
        /networkerror|failed to fetch|network request failed/i.test(message)
          ? 'Cannot reach the server right now. Please check the backend is running and try again.'
          : message
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bp-overlay" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        className="bp-modal flex max-w-2xl flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: 'calc(100vh - 40px)' }}
      >
        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
            <h2 className="text-xl font-bold text-slate-900">Create New RFP</h2>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              aria-label="Close"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {error && (
              <div className="bp-alert-danger animate-slide-up">
                <svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* Platform selector */}
            <div>
              <label className="mb-3 block text-sm font-semibold text-slate-800">
                1. Select Platform
              </label>
              <div className="grid grid-cols-3 gap-3">
                {PLATFORMS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPlatform(p.id)}
                    disabled={Boolean(p.disabled)}
                    title={p.disabled ? 'Coming soon' : undefined}
                    className={`flex items-center gap-2.5 rounded-xl border-2 px-3 py-2.5 text-left transition-all duration-150 ${
                      platform === p.id
                        ? p.activeBg + ' shadow-sm'
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                    } ${p.disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                  >
                    <span className={p.color}>{p.icon}</span>
                    <span className="text-sm font-semibold text-slate-900">{p.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* RFP text */}
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-800">
                2. Paste RFP Text
              </label>
              <textarea
                value={rfpText}
                onChange={(e) => setRfpText(e.target.value)}
                placeholder="Paste the full RFP text here. Include all requirements, questions, and context from the original document..."
                className="bp-textarea h-52 w-full font-mono text-sm"
                disabled={loading}
              />
              <div className="mt-2 flex items-center justify-between gap-3">
                <div className="flex-1 overflow-hidden rounded-full bg-slate-200 h-1.5">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${charColor}`}
                    style={{ width: `${charPct}%` }}
                  />
                </div>
                <span className={`text-xs font-medium ${charPct > 90 ? 'text-red-600' : 'text-slate-500'}`}>
                  {rfpText.length.toLocaleString()} chars
                </span>
              </div>
            </div>

            {platform === 'Umbrex' && (
              <div>
                <label htmlFor="bidder-notes" className="mb-1 block text-sm font-semibold text-slate-800">
                  3. DC notes <span className="font-normal text-slate-500">(optional)</span>
                </label>
                <p className="mb-2 text-xs text-slate-500">
                  Your brainstorm for this bid: which background, credentials, or past work to lead with. The project
                  selector and the proposal follow it.
                </p>
                <textarea
                  id="bidder-notes"
                  value={bidderNotes}
                  onChange={(e) => setBidderNotes(e.target.value.slice(0, MAX_NOTES_CHARS))}
                  placeholder="e.g. Lead with Stanford engineering and EPC work at McKinsey; mention public-private infrastructure research."
                  className="bp-textarea h-24 w-full text-sm"
                  disabled={loading}
                />
                <p className="mt-1 text-right text-xs text-slate-500">
                  {bidderNotes.length.toLocaleString()} / {MAX_NOTES_CHARS.toLocaleString()}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50/60 px-6 py-4">
            <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !rfpText.trim()}
              isLoading={loading}
            >
              {loading ? 'Creating RFP…' : 'Create RFP'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
